import React, { useState, useMemo, useEffect, useCallback } from "react";
import { format } from "date-fns";
import {
  Stack,
  Box,
  CircularProgress,
  InputAdornment,
  OutlinedInput,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  Add as AddIcon,
  KeyboardArrowDown as ArrowDownIcon,
  UploadFile as UploadFileIcon,
  DeleteSweep as DeleteSweepIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";
import { useAppSelector } from "@/store";
import {
  useGetProductsWithTariffsQuery,
  useLazyGetProductsWithTariffsQuery,
  ProductWithTariffs,
  ProductTariffQueryInput,
} from "@/services/product-catalogue/pc-api";
import useSnackbar from "@/hooks/useSnackbar";
import { useProductAnalysisPolling, useOrderedDutyColumns } from "@/hooks";
import { roles } from "@/roles";
import DatePicker from "@/components/date-picker";
import StatsBar from "@/components/stats-bar";
import ProductTable from "../../product-catalogue/components/product-table";
import CreateProductForm from "../../product-catalogue/components/create-product-form";
import EditProductForm from "../../product-catalogue/components/edit-product-form";
import DeleteProductDialog from "../../product-catalogue/components/delete-product-dialog";
import CSVBatchUpload from "../../product-catalogue/components/csv-batch-upload";
import BatchDeleteDialog from "../../product-catalogue/components/batch-delete-dialog";
import { tariff_code_placeholder } from "@/constants";
import { buildProductCsv } from "@/utils/product-csv-export";

const ProductsTab: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient, areClientsLoading } = useAppSelector(
    (state) => state.client
  );
  const { selectedTenant, areTenantsLoading } = useAppSelector(
    (state) => state.tenantManagement
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductWithTariffs | null>(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [isBatchDeleteOpen, setIsBatchDeleteOpen] = useState(false);
  const [batchDeleteMode, setBatchDeleteMode] = useState<"selected" | "all">("selected");
  const [allProductIds, setAllProductIds] = useState<string[]>([]);
  const [isFetchingAllIds, setIsFetchingAllIds] = useState(false);

  const { show: toast } = useSnackbar();

  const [triggerGetProducts] = useLazyGetProductsWithTariffsQuery();
  const { orderedColumns: dutyColumns } = useOrderedDutyColumns();

  // Listen for create product event from dashboard
  useEffect(() => {
    const handleOpenCreateProduct = () => {
      setIsCreateFormOpen(true);
    };

    window.addEventListener('td:open-create-product', handleOpenCreateProduct);
    return () => {
      window.removeEventListener('td:open-create-product', handleOpenCreateProduct);
    };
  }, []);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  // Get client ID
  const clientId = useMemo(() => {
    if (isTDAdminTCD) {
      return selectedTenant?._id;
    }
    if (selectedClient?._id) return selectedClient._id;
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
      return user?._id;
    }
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  const queryParams = useMemo(() => {
    return {
      effectiveDate: effectiveDate,
      pageNo: currentPage,
      pageSize: pageSize,
      ...(clientId ? { clientId } : {}),
      ...(isTDAdminTCD && { tenantId: selectedTenant?._id }),
    };
  }, [effectiveDate, currentPage, pageSize, clientId, isTDAdminTCD, selectedTenant]);

  // Initialize polling system
  const polling = useProductAnalysisPolling({
    queryParams,
    enabled: true
  });

  const commonOptions = useMemo(() => {
    if (isTDAdminTCD) {
      return {
        skip: !selectedTenant || !clientId,
        refetchOnMountOrArgChange: true,
      };
    }
    return {
      skip: (canSelectClients && !selectedClient) || !clientId,
      refetchOnMountOrArgChange: true,
    };
  }, [canSelectClients, selectedClient, selectedTenant, isTDAdminTCD, clientId]);

  const {
    data: productsData,
    isLoading: isProductsLoading,
    isFetching: isProductsFetching,
  } = useGetProductsWithTariffsQuery(queryParams, commonOptions);

  const isLoading = useMemo(() => {
    return [isProductsLoading, areClientsLoading, areTenantsLoading].some(Boolean);
  }, [isProductsLoading, areClientsLoading, areTenantsLoading]);

  const filteredProducts = useMemo(() => {
    if (!productsData?.data) return [];
    if (!searchQuery.trim()) return productsData.data;

    const query = searchQuery.toLowerCase().trim();
    return productsData.data.filter((product) => {
      const skuMatch = product.sku?.toLowerCase().includes(query);
      const nameMatch = product.name?.toLowerCase().includes(query);
      const descriptionMatch = product.description?.toLowerCase().includes(query);
      return skuMatch || nameMatch || descriptionMatch;
    });
  }, [productsData?.data, searchQuery]);

  const stats = useMemo(() => {
    if (!productsData?.data) return { totalProducts: 0, uniqueCountries: 0, uniqueHTS: 0, avgTariffRate: 0 };

    const products = productsData.data;
    const countries = new Set(products.map(p => p.countryOfOrigin));
    const htsCodes = new Set(products.map(p => p.htsCode));

    const totalTariffRate = products.reduce((sum, product) => {
      const productTotal = product.additionalTariffs.reduce((pSum, tariff) => pSum + tariff.rate, 0);
      return sum + productTotal;
    }, 0);

    return {
      totalProducts: productsData.meta.countDocs || 0,
      uniqueCountries: countries.size,
      uniqueHTS: htsCodes.size,
      avgTariffRate: products.length > 0 ? (totalTariffRate / products.length) * 100 : 0,
    };
  }, [productsData]);

  const handleDateChange = (date: string | null) => {
    if (date) {
      setEffectiveDate(date);
    }
    setCurrentPage(1);
    setSelectedProductIds(new Set());
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedProductIds(new Set());
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    setSelectedProductIds(new Set());
  };

  const handleEditProduct = (product: ProductWithTariffs) => {
    setSelectedProduct(product);
    setIsEditFormOpen(true);
  };

  const handleDeleteProduct = (productId: string) => {
    const product = productsData?.data.find(p => p._id === productId);
    if (product) {
      setSelectedProduct(product);
      setIsDeleteDialogOpen(true);
    }
  };

  const handleSelectionChange = useCallback((ids: Set<string>) => {
    setSelectedProductIds(ids);
  }, []);

  const handleDeleteSelected = () => {
    if (selectedProductIds.size === 0) return;
    setBatchDeleteMode("selected");
    setIsBatchDeleteOpen(true);
  };

  const handleClearProductHub = async () => {
    if (!productsData?.data.length) return;
    setIsFetchingAllIds(true);
    try {
      const fetchPageSize = 500;
      const allIds: string[] = [];
      const { pageNo, pageSize: _ps, ...baseQuery } = queryParams;

      const first = await triggerGetProducts({ ...baseQuery, pageNo: 1, pageSize: fetchPageSize } as ProductTariffQueryInput).unwrap();
      allIds.push(...(first?.data?.map(p => p._id) ?? []));
      const total = first?.meta?.countDocs ?? allIds.length;
      const totalPages = Math.max(1, Math.ceil(total / fetchPageSize));
      for (let p = 2; p <= totalPages; p++) {
        const res = await triggerGetProducts({ ...baseQuery, pageNo: p, pageSize: fetchPageSize } as ProductTariffQueryInput).unwrap();
        allIds.push(...(res?.data?.map(p => p._id) ?? []));
      }

      setAllProductIds(allIds);
      setBatchDeleteMode("all");
      setIsBatchDeleteOpen(true);
    } catch (error) {
      console.error("Error fetching all product IDs:", error);
      toast("Failed to fetch product list", "", "error");
    } finally {
      setIsFetchingAllIds(false);
    }
  };

  const handleBatchDeleteComplete = () => {
    setIsBatchDeleteOpen(false);
    setSelectedProductIds(new Set());
    const count = batchDeleteMode === "all"
      ? allProductIds.length
      : selectedProductIds.size;
    toast(`Successfully deleted ${count} product${count !== 1 ? "s" : ""}`, "", "success");
  };

  const handleCloseBatchDelete = () => {
    setIsBatchDeleteOpen(false);
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const exportPageSize = 500;
      const allProducts: ProductWithTariffs[] = [];
      const { pageNo, pageSize, ...downloadBaseQuery } = queryParams;

      const first = await triggerGetProducts({ ...downloadBaseQuery, pageNo: 1, pageSize: exportPageSize } as ProductTariffQueryInput).unwrap();
      allProducts.push(...(first?.data ?? []));
      const total = first?.meta?.countDocs ?? allProducts.length;
      const totalPages = Math.max(1, Math.ceil(total / exportPageSize));
      for (let p = 2; p <= totalPages; p++) {
        const res = await triggerGetProducts({ ...downloadBaseQuery, pageNo: p, pageSize: exportPageSize } as ProductTariffQueryInput).unwrap();
        allProducts.push(...(res?.data ?? []));
      }

      if (allProducts.length === 0) return;

      const csvContent = buildProductCsv(allProducts, dutyColumns);

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "product_catalogue.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Stack sx={{
      height: "100%",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      flex: 1,
    }}>
      {/* Stats row */}
      <Box sx={{ flexShrink: 0, px: 3, pt: 2, pb: 0.5 }}>
        <StatsBar
          isLoading={isLoading}
          compact
          items={[
            { label: "Products", value: stats.totalProducts.toLocaleString(), centerValue: true },
            { label: "Countries", value: stats.uniqueCountries, centerValue: true },
            { label: "HTS Codes", value: stats.uniqueHTS, centerValue: true },
            { label: "Avg. Tariff", value: `${stats.avgTariffRate.toFixed(1)}%`, color: tokens.text.primary },
          ]}
        />
      </Box>

      {/* Toolbar row: Search + Actions */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{ flexShrink: 0, px: 3, pt: 1, pb: 2, gap: 1.5 }}
      >
        {/* Search */}
        <OutlinedInput
          size="small"
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
            setSelectedProductIds(new Set());
          }}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon sx={{ color: tokens.text.muted, fontSize: 18 }} />
            </InputAdornment>
          }
          sx={{
            backgroundColor: tokens.bg.surface,
            borderRadius: tokens.radius.md,
            minWidth: 240,
            height: 36,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.border.default,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.text.disabled,
            },
            '& .MuiOutlinedInput-input': {
              py: 0.75,
              fontSize: tokens.fontSize.md,
            },
          }}
        />

        {/* Date - next to search */}
        <DatePicker
          value={effectiveDate}
          onChangeValue={handleDateChange}
          sx={{
            minWidth: 150,
            '& .MuiOutlinedInput-root': {
              backgroundColor: tokens.bg.surface,
              height: 36,
              borderRadius: tokens.radius.md,
              '& fieldset': { borderColor: tokens.border.default },
              '&:hover fieldset': { borderColor: tokens.text.disabled },
            },
            '& .MuiOutlinedInput-input': {
              py: 0.75,
              fontSize: tokens.fontSize.md,
            },
          }}
        />

        {/* Actions - pushed right */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={isDownloading ? <CircularProgress size={16} /> : <FileDownloadIcon sx={{ fontSize: 18 }} />}
            onClick={handleDownload}
            disabled={isDownloading || !productsData?.data.length}
            sx={{
              borderColor: tokens.border.default,
              color: tokens.text.body,
              "&:hover": {
                borderColor: tokens.text.disabled,
                backgroundColor: tokens.bg.hover,
              },
              borderRadius: tokens.radius.md,
              fontWeight: tokens.fontWeight.bold,
              whiteSpace: 'nowrap',
              px: 1.5,
              height: 36,
              fontSize: tokens.fontSize.md,
              minWidth: 'auto',
            }}
          >
            {isDownloading ? 'Downloading...' : 'Export'}
          </Button>
          <Button
            variant="outlined"
            startIcon={isFetchingAllIds ? <CircularProgress size={16} /> : <DeleteSweepIcon sx={{ fontSize: 18 }} />}
            onClick={handleClearProductHub}
            disabled={!productsData?.data.length || isFetchingAllIds}
            sx={{
              borderColor: tokens.status.dangerBg,
              color: tokens.status.danger,
              "&:hover": {
                borderColor: tokens.status.danger,
                backgroundColor: tokens.status.dangerBg,
              },
              borderRadius: tokens.radius.md,
              fontWeight: tokens.fontWeight.bold,
              whiteSpace: 'nowrap',
              px: 1.5,
              height: 36,
              fontSize: tokens.fontSize.md,
              minWidth: 'auto',
            }}
          >
            {isFetchingAllIds ? 'Loading...' : 'Clear All'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            endIcon={<ArrowDownIcon sx={{ fontSize: 18 }} />}
            onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
            sx={{
              bgcolor: tokens.brand.primary,
              "&:hover": { bgcolor: tokens.brand.primaryHover },
              borderRadius: tokens.radius.md,
              fontWeight: tokens.fontWeight.bold,
              whiteSpace: 'nowrap',
              px: 2,
              height: 36,
              fontSize: tokens.fontSize.md,
              boxShadow: 'none',
            }}
          >
            Create
          </Button>
          <Menu
            anchorEl={createMenuAnchor}
            open={Boolean(createMenuAnchor)}
            onClose={() => setCreateMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              '& .MuiPaper-root': {
                borderRadius: tokens.radius.md,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                mt: 0.5,
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setCreateMenuAnchor(null);
                setIsCreateFormOpen(true);
              }}
              sx={{ py: 1.25, px: 2 }}
            >
              <ListItemIcon>
                <AddIcon sx={{ fontSize: 20, color: tokens.brand.primary }} />
              </ListItemIcon>
              <ListItemText
                primary="Add Product"
                secondary="Create a single product"
                primaryTypographyProps={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.semibold }}
                secondaryTypographyProps={{ fontSize: tokens.fontSize.base }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setCreateMenuAnchor(null);
                setIsBulkUploadOpen(true);
              }}
              sx={{ py: 1.25, px: 2 }}
            >
              <ListItemIcon>
                <UploadFileIcon sx={{ fontSize: 20, color: tokens.brand.primary }} />
              </ListItemIcon>
              <ListItemText
                primary="Batch Upload"
                secondary="Upload products via CSV"
                primaryTypographyProps={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.semibold }}
                secondaryTypographyProps={{ fontSize: tokens.fontSize.base }}
              />
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {/* Selection Action Bar */}
      {selectedProductIds.size > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            backgroundColor: tokens.brand.primary,
            borderRadius: tokens.radius.md,
            px: 2.5,
            py: 1,
            mx: 3,
            mb: 1,
          }}
        >
          <Typography sx={{ color: 'white', fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.md }}>
            {selectedProductIds.size} product{selectedProductIds.size !== 1 ? 's' : ''} selected
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={handleDeleteSelected}
              sx={{
                backgroundColor: tokens.status.danger,
                color: 'white',
                fontWeight: tokens.fontWeight.bold,
                borderRadius: tokens.radius.md,
                fontSize: tokens.fontSize.sm,
                px: 1.5,
                py: 0.5,
                '&:hover': { backgroundColor: tokens.status.dangerHover },
              }}
            >
              Delete Selected
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              onClick={() => setSelectedProductIds(new Set())}
              sx={{
                borderColor: 'rgba(255,255,255,0.4)',
                color: 'white',
                fontWeight: tokens.fontWeight.semibold,
                borderRadius: tokens.radius.md,
                fontSize: tokens.fontSize.sm,
                px: 1.5,
                py: 0.5,
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Clear Selection
            </Button>
          </Stack>
        </Stack>
      )}

      {/* Table Section — rounded card, fills remaining height */}
      <Box sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}>
        <Stack sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          mx: 3,
          mb: 2,
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.border.container}`,
          bgcolor: tokens.bg.surface,
          overflow: "hidden",
          boxShadow: tokens.shadow.sm,
        }}>
          {isLoading ? (
            <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: tokens.radius.lg }} />
          ) : filteredProducts.length === 0 && !isProductsFetching && !searchQuery.trim() ? (
            <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ flex: 1, py: 6 }}>
              <img src={tariff_code_placeholder} alt="No products" style={{ width: 120, height: 120, opacity: 0.6 }} />
              <Typography variant="h6" fontWeight={tokens.fontWeight.semibold} color={tokens.text.primary}>
                No Products Yet
              </Typography>
              <Typography variant="body2" sx={{ color: tokens.text.secondary, maxWidth: 420, textAlign: 'center', lineHeight: 1.6 }}>
                Add and manage your products to calculate duties, verify HTS codes, assess compliance risk, and get AI-powered risk scores.
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreateFormOpen(true)}
                  sx={{
                    bgcolor: tokens.brand.primary,
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: tokens.fontWeight.semibold,
                    borderRadius: tokens.radius.md,
                    px: 3,
                    '&:hover': { bgcolor: tokens.brand.primaryHover },
                  }}
                >
                  Add Product
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setIsBulkUploadOpen(true)}
                  sx={{
                    borderColor: tokens.border.default,
                    color: tokens.text.body,
                    textTransform: 'none',
                    fontWeight: tokens.fontWeight.semibold,
                    borderRadius: tokens.radius.md,
                    px: 3,
                    '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
                  }}
                >
                  Batch Upload
                </Button>
              </Stack>
            </Stack>
          ) : (
            <ProductTable
              data={filteredProducts}
              currentPage={currentPage}
              pageSize={pageSize}
              isLoading={isProductsFetching}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              totalCount={searchQuery.trim() ? filteredProducts.length : (productsData?.meta.countDocs ?? 0)}
              onEditProduct={handleEditProduct}
              onDeleteProduct={handleDeleteProduct}
              isProductPending={polling.isProductPending}
              selectedIds={selectedProductIds}
              onSelectionChange={handleSelectionChange}
            />
          )}
        </Stack>
      </Box>

      {/* Dialogs */}
      <CreateProductForm
        open={isCreateFormOpen}
        onClose={() => setIsCreateFormOpen(false)}
        onProductCreated={() => setIsCreateFormOpen(false)}
        onTriggerPolling={polling.addProductPolling}
      />

      <EditProductForm
        open={isEditFormOpen}
        onClose={() => {
          setIsEditFormOpen(false);
          setSelectedProduct(null);
        }}
        onProductUpdated={() => {
          setIsEditFormOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        onTriggerPolling={polling.addProductPolling}
      />

      <DeleteProductDialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedProduct(null);
        }}
        onProductDeleted={() => {
          setIsDeleteDialogOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
      />

      <CSVBatchUpload
        open={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onSuccess={() => setIsBulkUploadOpen(false)}
        onTriggerBulkPolling={polling.addBulkPolling}
      />

      <BatchDeleteDialog
        open={isBatchDeleteOpen}
        onClose={handleCloseBatchDelete}
        onDeleted={handleBatchDeleteComplete}
        productIds={batchDeleteMode === "all"
          ? allProductIds
          : Array.from(selectedProductIds)}
        mode={batchDeleteMode}
        totalProductCount={productsData?.meta.countDocs}
        clientId={clientId}
      />
    </Stack>
  );
};

export default ProductsTab;
